import type { CampaignRaceDto, PartyPrimary, Race, RaceStage, WriteInRequest } from '@signalvote/shared';
import { cycleOfDate, DateConfidence, placesOf, RaceStageKind } from '@signalvote/shared';
import { US_STATE_OPTIONS } from '@/lib/usStates';

/** RaceLink.AsksPrimary on the server, which stays the authority. */
export function asksPrimary(race: Race): boolean {
  return race.stages.some((s) => s.kind === RaceStageKind.RepublicanPrimary) && race.stages.some((s) => s.kind === RaceStageKind.DemocraticPrimary);
}

/**
 * The stages a goal can be saved for, as RaceLink.CampaignStages picks them for a campaign not yet made: the answered party
 * primary or the Race's one primary, then the General.
 */
export function campaignStages(race: Race, primary: PartyPrimary | null): RaceStage[] {
  const primaryKind = asksPrimary(race)
    ? primary && PRIMARY_STAGE[primary]
    : race.stages.find((s) => s.kind !== RaceStageKind.General)?.kind;
  return race.stages.filter((s) => s.kind === RaceStageKind.General || s.kind === primaryKind);
}

const PRIMARY_STAGE: Record<PartyPrimary, RaceStageKind> = {
  Republican: RaceStageKind.RepublicanPrimary,
  Democratic: RaceStageKind.DemocraticPrimary,
};

/** A Race id starts with its state, lowercase: "nc-house-1". */
export const stateOf = (raceId: string) => raceId.split('-')[0].toUpperCase();

export const stateName = (code: string) => US_STATE_OPTIONS.find((s) => s.value === code)?.label ?? code;

export const stateNameOf = (raceId: string) => stateName(stateOf(raceId));

/**
 * The Race the server builds from a write-in (WriteIn.Build), for the calculator to run on before sign-up sends it. The id is
 * a stand-in: the server gives the Race its own.
 */
export function writtenRace(writeIn: WriteInRequest): Race {
  const { state, name, electionDate, seats } = writeIn;
  return {
    id: `${state.toLowerCase()}-write-in:`,
    name,
    office: name,
    place: stateName(state),
    searchNames: [],
    sources: [],
    stages: [
      {
        status: 'Refused',
        kind: RaceStageKind.General,
        cycle: cycleOfDate(electionDate),
        rule: seats === 1 ? { kind: 'MostVotes' } : { kind: 'VoteForSeveral', seats },
        ruleText: null,
        lastResult: null,
        notes: [],
        date: electionDate,
        dateConfidence: DateConfidence.Likely,
        reason: 'We don’t cover this race, so the numbers are yours.',
      },
    ],
  };
}

/** A written-in link's Race, back in the form the write-in form edits. */
export function writeInOf(link: CampaignRaceDto): WriteInRequest | null {
  const general = link.race.stages.find((s) => s.kind === RaceStageKind.General);
  if (!link.writtenIn || !general?.date) return null;
  return { state: stateOf(link.raceId), name: link.race.name, electionDate: general.date, seats: placesOf(general.rule) ?? 1 };
}
